import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type React from 'react';
import { Children, type CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const CARD_WIDTH = 224;
const GAP = 10;

/**
 * Horizontal inset per slide so box-shadows and -right-2 badges stay inside the
 * Embla viewport instead of being clipped by overflow-hidden.
 */
const SLIDE_BLEED_X_CLASS = 'px-2';

/**
 * Vertical inset per slide so -top-2 badges and bottom shadows stay visible.
 * Kept even when {@link CarouselProps.flushSlides} is true — horizontal flush
 * must not sacrifice badge clearance.
 */
const SLIDE_BLEED_Y_CLASS = 'pt-3 pb-1';

/** Right-only bleed when slides are horizontally flush with surrounding headings. */
const SLIDE_BLEED_END_CLASS = 'pe-2';

const SLIDE_BLEED_PADDING_CLASS = cn(SLIDE_BLEED_X_CLASS, SLIDE_BLEED_Y_CLASS);

/**
 * Responsive top padding for the Embla viewport itself.
 * This is the visual gap between the content above the carousel and the first card.
 * Keep separate from SLIDE_BLEED_PADDING_CLASS — they serve different layout roles.
 */
const VIEWPORT_TOP_PADDING_CLASS = 'pt-1 md:pt-3';

const SINGLE_SLIDE_MAX_WIDTH = '33%';

function getSlideStyle(
	slideCount: number,
	expandSlidesToFill: boolean,
	cardWidthPx: number,
	gapPx: number
): CSSProperties {
	if (slideCount === 1) {
		return {
			minWidth: 0,
			flex: '0 0 auto',
			width: '100%',
			maxWidth: SINGLE_SLIDE_MAX_WIDTH
		};
	}
	if (expandSlidesToFill) {
		return { minWidth: 0, flex: '1 1 0', paddingLeft: gapPx };
	}
	return { minWidth: 0, flex: `0 0 ${cardWidthPx + gapPx}px`, paddingLeft: gapPx };
}

interface CarouselProps {
	children: React.ReactNode;
	cardWidthPx?: number;
	showButtonsWhenContentFits?: boolean;
	/** Force all slides to expand and fill the container width, regardless of viewport size. */
	forceExpand?: boolean;
	/** Drop horizontal slide/viewport inset so the first card aligns with surrounding headings. Vertical bleed for badges is kept. */
	flushSlides?: boolean;
	/** Horizontal space between slides; defaults to {@link GAP}. */
	slideGapPx?: number;
	className?: string;
	testId?: string;
}

function Carousel({
	children,
	cardWidthPx = CARD_WIDTH,
	showButtonsWhenContentFits = false,
	forceExpand = false,
	flushSlides = false,
	slideGapPx = GAP,
	className = '',
	testId
}: CarouselProps): React.JSX.Element {
	const { t } = useTranslation();
	const slides = Children.toArray(children);
	const slideCount = slides.length;

	const [emblaRef, emblaApi] = useEmblaCarousel({
		align: slideCount === 1 ? 'center' : 'start',
		loop: false,
		skipSnaps: false,
		containScroll: 'trimSnaps'
	});
	const [canScrollPrev, setCanScrollPrev] = useState(false);
	const [canScrollNext, setCanScrollNext] = useState(false);
	const [expandSlidesToFill, setExpandSlidesToFill] = useState(forceExpand);
	const viewportRef = useRef<HTMLDivElement | null>(null);

	const setViewportRef = useCallback(
		(node: HTMLDivElement | null) => {
			viewportRef.current = node;
			emblaRef(node);
		},
		[emblaRef]
	);

	const updateButtonState = useCallback(() => {
		if (!emblaApi) return;
		setCanScrollPrev(emblaApi.canScrollPrev());
		setCanScrollNext(emblaApi.canScrollNext());
	}, [emblaApi]);

	const goToPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
	const goToNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

	useEffect(() => {
		if (!emblaApi) return;
		updateButtonState();
		emblaApi.on('select', updateButtonState);
		emblaApi.on('reInit', updateButtonState);
		return () => {
			emblaApi.off('select', updateButtonState);
			emblaApi.off('reInit', updateButtonState);
		};
	}, [emblaApi, updateButtonState]);

	useEffect(() => {
		if (forceExpand) {
			setExpandSlidesToFill(true);
			return;
		}
		const el = viewportRef.current;
		if (!el || slideCount <= 1) {
			setExpandSlidesToFill(false);
			return;
		}

		function measure(): void {
			const node = viewportRef.current;
			if (!node) return;
			const vw = node.clientWidth;
			const fixedTotal = slideCount * (cardWidthPx + slideGapPx);
			setExpandSlidesToFill(vw + 0.5 >= fixedTotal);
		}

		measure();
		const ro = new ResizeObserver(measure);
		ro.observe(el);
		return () => ro.disconnect();
	}, [forceExpand, slideCount, cardWidthPx, slideGapPx]);

	// Embla must re-measure when slide flex basis or count changes (not only when api reference updates).
	// biome-ignore lint/correctness/useExhaustiveDependencies: expandSlidesToFill, slideCount, cardWidthPx and slideGapPx drive layout
	useEffect(() => {
		emblaApi?.reInit();
	}, [emblaApi, expandSlidesToFill, slideCount, cardWidthPx, slideGapPx]);

	const slideStyle = getSlideStyle(slideCount, expandSlidesToFill, cardWidthPx, slideGapPx);

	return (
		<div className={cn('relative', className)} data-testid={testId}>
			{(showButtonsWhenContentFits || canScrollPrev) && (
				<Button
					type="button"
					variant="outline"
					size="icon"
					className="absolute left-0 top-1/2 z-10 -translate-y-1/2 -translate-x-2 rounded-full border-border bg-background/95 shadow-md hover:bg-muted"
					onClick={goToPrev}
					disabled={!canScrollPrev}
					aria-label={t('common.previous')}
					data-testid={testId ? `${testId}-previous` : undefined}
				>
					<ChevronLeft className="h-5 w-5" />
				</Button>
			)}
			<div
				className={cn('overflow-hidden', !flushSlides && VIEWPORT_TOP_PADDING_CLASS)}
				ref={setViewportRef}
				data-testid={testId ? `${testId}-viewport` : undefined}
			>
				<div
					className={cn('flex touch-pan-y items-stretch', slideCount === 1 && 'justify-center')}
					style={{ marginLeft: slideCount === 1 ? 0 : -slideGapPx }}
				>
					{slides.map((child, index) => {
						const key =
							typeof child === 'object' && child !== null && 'key' in child
								? ((child as React.ReactElement).key ?? index)
								: index;
						return (
							<div key={key} style={slideStyle}>
								<div
									className={cn(
										flushSlides ? SLIDE_BLEED_Y_CLASS : SLIDE_BLEED_PADDING_CLASS,
										flushSlides && SLIDE_BLEED_END_CLASS,
										'h-full'
									)}
								>
									{child}
								</div>
							</div>
						);
					})}
				</div>
			</div>
			{(showButtonsWhenContentFits || canScrollNext) && (
				<Button
					type="button"
					variant="outline"
					size="icon"
					className="absolute right-0 top-1/2 z-10 -translate-y-1/2 translate-x-2 rounded-full border-border bg-background/95 shadow-md hover:bg-muted"
					onClick={goToNext}
					disabled={!canScrollNext}
					aria-label={t('common.next')}
					data-testid={testId ? `${testId}-next` : undefined}
				>
					<ChevronRight className="h-5 w-5" />
				</Button>
			)}
		</div>
	);
}

export default Carousel;
export { CARD_WIDTH, GAP };
